import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, errMsg } from "../lib/api";
import { StatCard } from "../components/app/StatCard";
import { DistChart } from "../components/app/DistChart";
import { StatusBadge } from "../components/app/PlanForm";

const Box = ({ title, children, testId }) => (
  <section className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid={testId}>
    <h2 className="mb-3 font-serif-display text-xl font-semibold text-amber-900">{title}</h2>{children}
  </section>
);

const Strengths = ({ gkk, canEdit, onSaved }) => {
  const [f, setF] = useState({ strengths: gkk.strengths || "", resources: gkk.resources || "" });
  const save = async () => { try { await api.patch(`/gkk-profiles/${gkk.gkk_id}`, f); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  if (!canEdit) return <><p className="text-sm text-stone-700 whitespace-pre-wrap">{gkk.strengths || "Not yet described."}</p><p className="mt-2 text-sm text-stone-600 whitespace-pre-wrap">{gkk.resources}</p></>;
  return (
    <div className="space-y-3">
      <Textarea rows={3} placeholder="Community strengths (active ministries, leaders, traditions…)" value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} data-testid="strengths-input" />
      <Textarea rows={3} placeholder="Available resources (chapel, meeting place, volunteers, partners…)" value={f.resources} onChange={(e) => setF({ ...f, resources: e.target.value })} data-testid="resources-input" />
      <Button size="sm" variant="outline" onClick={save} data-testid="strengths-save">Save</Button>
    </div>
  );
};

const PlanList = ({ items }) => items.length ? (
  <ul className="space-y-2">{items.map((p) => <li key={p.plan_id}><Link to={`/plans/${p.plan_id}`} className="flex items-center justify-between gap-2 text-sm hover:text-amber-900"><span>{p.intervention}</span><StatusBadge s={p.status} /></Link></li>)}</ul>
) : <p className="text-sm text-stone-500">None yet.</p>;

export default function GkkProfile() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/gkk-profiles/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="text-sm text-stone-600" data-testid="profile-error">{err}</p>;
  if (!d) return null;
  const g = d.gkk;
  return (
    <div className="space-y-6">
      <div>
        <Link to="/gkk-profiles" className="text-sm text-amber-900" data-testid="back-to-profiles">← All GKK profiles</Link>
        <p className="mt-3 text-xs uppercase tracking-widest font-semibold text-amber-800/80">{g.code} · {g.zone}</p>
        <h1 className="mt-1 font-serif-display text-3xl sm:text-4xl font-bold text-amber-950" data-testid="profile-title">{g.patron || g.name}</h1>
        <p className="text-sm text-stone-600">{g.local_area}{g.address ? ` · ${g.address}` : ""}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Households surveyed (all periods)" value={d.totals.households} testId="profile-households" />
        <StatCard label="People in surveyed households" value={d.totals.individuals_in_households} testId="profile-individuals" />
        <StatCard label="Ongoing initiatives" value={d.plans.ongoing.length} testId="profile-ongoing" />
      </div>
      {d.suppressed_all && <p className="rounded-lg bg-stone-100 p-4 text-sm text-stone-600" data-testid="profile-suppressed">Fewer than 5 households surveyed — details are hidden to protect privacy.</p>}
      {d.totals.households === 0 && <p className="rounded-lg bg-stone-100 p-4 text-sm text-stone-600" data-testid="profile-no-data">No survey records yet for this GKK. This is a data gap, not a finding.</p>}
      <div className="grid gap-6 lg:grid-cols-2">
        <Box title="Areas requiring further attention" testId="profile-attention">
          <p className="mb-2 text-xs text-stone-500">From survey period {d.attention_period || "—"}. For discernment, not judgment.</p>
          <ul className="space-y-2">{d.attention.map((a) => <li key={a.rule_key} className="text-sm"><b className="text-stone-800">{a.title}</b><p className="text-xs text-stone-600">{a.evidence}</p></li>)}</ul>
          {!d.attention.length && <p className="text-sm text-stone-500">Not enough responses to show findings.</p>}
        </Box>
        <Box title="Community strengths & resources" testId="profile-strengths"><Strengths gkk={g} canEdit={d.can_edit} onSaved={load} /></Box>
      </div>
      {d.distributions.concerns && (
        <div className="grid gap-6 lg:grid-cols-2">
          <p className="lg:col-span-2 text-xs text-stone-500">Charts below combine all survey periods ({d.data_source.periods.join(", ") || "none"}).</p>
          <DistChart title="Dominant community needs" dist={d.distributions.concerns} testId="profile-concerns" />
          <DistChart title="Pastoral assistance requested" dist={d.distributions.assistance_requested} testId="profile-assistance" />
          <DistChart title="Sunday Mass participation" dist={d.distributions.mass_attendance} testId="profile-mass" />
          <DistChart title="Employment & livelihood" dist={d.distributions.employment_status} testId="profile-employment" />
        </div>
      )}
      {d.trends.length > 1 && (
        <Box title="Changes over time" testId="profile-trends">
          <div className="h-60"><ResponsiveContainer><LineChart data={d.trends}><CartesianGrid stroke="#F5F0E6" /><XAxis dataKey="period" /><YAxis unit="%" domain={[0, 100]} /><Tooltip />
            <Line dataKey="weekly_mass_pct" name="Weekly Mass %" stroke="#78350F" strokeWidth={2} /><Line dataKey="unemployed_pct" name="Unemployed head %" stroke="#D97706" strokeWidth={2} /></LineChart></ResponsiveContainer></div>
        </Box>
      )}
      <div className="grid gap-6 lg:grid-cols-3">
        <Box title="Proposed initiatives" testId="profile-plans-proposed"><PlanList items={d.plans.proposed} /></Box>
        <Box title="Approved & ongoing" testId="profile-plans-ongoing"><PlanList items={d.plans.ongoing} /></Box>
        <Box title="Completed programs" testId="profile-plans-completed"><PlanList items={d.plans.completed} /></Box>
      </div>
      <p className="text-xs italic text-stone-500">{d.caveat}</p>
    </div>
  );
}
